import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";

const BRAND_BLUE = "#00336C";
const PDF_MIME_TYPE = "application/pdf";

function formatUploadDate(value) {
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return "Unknown upload date";
  return new Date(timestamp).toLocaleString();
}

function getUploadTimestamp(document) {
  const timestamp = new Date(document?.uploaded_at).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function getErrorMessage(error, fallback) {
  return error?.response?.data?.detail || error?.message || fallback;
}

function CompanyDocumentsPanel({
  companyId,
  documents = [],
  uploadedAt,
  labels,
  onUpload,
  onDownload,
  onDelete,
  onCompanyUpdated,
  downloadBlobFile,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [viewingId, setViewingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  const sortedDocuments = useMemo(
    () =>
      Array.isArray(documents)
        ? [...documents].sort(
            (a, b) =>
              getUploadTimestamp(b) - getUploadTimestamp(a),
          )
        : [],
    [documents],
  );

  const hasDocuments = sortedDocuments.length > 0;
  const busy = uploading || deletingId != null || viewingId != null || downloadingId != null;

  const handleChooseFile = () => {
    if (busy) return;
    fileInputRef.current?.click();
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!String(file.name || "").toLowerCase().endsWith(".pdf")) {
      setError(labels.invalidTypeMessage);
      return;
    }

    setUploading(true);
    setError("");

    try {
      const response = await onUpload(companyId, file);
      onCompanyUpdated?.(response.data);
    } catch (err) {
      setError(getErrorMessage(err, labels.uploadErrorMessage));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (document) => {
    setDownloadingId(document.id);
    setError("");

    try {
      const response = await onDownload(companyId, document.id);
      downloadBlobFile(response.data, document.original_name || labels.defaultFileName);
    } catch (err) {
      setError(getErrorMessage(err, labels.downloadErrorMessage));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleView = async (document) => {
    const previewWindow = window.open("", "_blank");
    if (!previewWindow) {
      setError("Popup blocked. Please allow popups and try again.");
      return;
    }

    previewWindow.opener = null;
    previewWindow.document.title = labels.previewTitle;

    setViewingId(document.id);
    setError("");

    try {
      const response = await onDownload(companyId, document.id);
      const fileUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: PDF_MIME_TYPE }),
      );
      previewWindow.location.href = fileUrl;

      window.setTimeout(() => {
        window.URL.revokeObjectURL(fileUrl);
      }, 60_000);
    } catch (err) {
      previewWindow.close();
      setError(getErrorMessage(err, labels.viewErrorMessage));
    } finally {
      setViewingId(null);
    }
  };

  const handleDelete = async (document) => {
    setDeletingId(document.id);
    setError("");

    try {
      const response = await onDelete(companyId, document.id);
      onCompanyUpdated?.(response.data);
    } catch (err) {
      setError(getErrorMessage(err, labels.deleteErrorMessage));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        hidden
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleUpload}
      />

      <Typography variant="body1" sx={{ color: BRAND_BLUE }}>
        {labels.description}
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#fafafa",
          color: BRAND_BLUE,
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: BRAND_BLUE }}
            >
              {labels.title}
            </Typography>
            <Typography variant="body2" sx={{ color: BRAND_BLUE }}>
              {hasDocuments
                ? `${sortedDocuments.length} PDF${
                    sortedDocuments.length === 1 ? "" : "s"
                  } uploaded`
                : labels.emptyMessage}
            </Typography>
            {uploadedAt && hasDocuments && (
              <Typography variant="body2" sx={{ color: BRAND_BLUE }}>
                Most recent upload {formatUploadDate(uploadedAt)}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={
                uploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CloudUploadIcon />
                )
              }
              onClick={handleChooseFile}
              disabled={busy}
            >
              {hasDocuments ? "Add PDF" : "Upload PDF"}
            </Button>
          </Box>

          {hasDocuments ? (
            <Stack spacing={1.5}>
              {sortedDocuments.map((document, index) => (
                <Box key={document.id || `${document.original_name || "document"}-${index}`}>
                  {index > 0 && <Divider sx={{ mb: 1.5 }} />}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: BRAND_BLUE }}
                      >
                        {document.original_name || labels.defaultFileName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: BRAND_BLUE }}>
                        Uploaded {formatUploadDate(document.uploaded_at)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        startIcon={
                          viewingId === document.id ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <VisibilityIcon />
                          )
                        }
                        onClick={() => handleView(document)}
                        disabled={busy}
                      >
                        View
                      </Button>

                      <Button
                        variant="outlined"
                        startIcon={
                          downloadingId === document.id ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                        onClick={() => handleDownload(document)}
                        disabled={busy}
                      >
                        Download
                      </Button>

                      <Button
                        color="error"
                        variant="outlined"
                        startIcon={
                          deletingId === document.id ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <DeleteOutlineIcon />
                          )
                        }
                        onClick={() => handleDelete(document)}
                        disabled={busy}
                      >
                        {labels.deleteButtonLabel}
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </Box>
    </>
  );
}

export default CompanyDocumentsPanel;
